import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Descriptions, Button, Spin, message } from 'antd';
import dayjs from 'dayjs';
import followUpService, { FollowUp, FollowUpRecord } from '../services/followUp';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SERVICE_TYPE_OPTIONS from '../config/service-types';
import { INTENT_OPTIONS, STATUS_OPTIONS } from '../config/follow-up-options';

// 客户跟进详情页面：展示单条跟进记录的详细信息
const FollowUpDetail: React.FC = () => {
  useDocumentTitle('跟进详情');
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [followUp, setFollowUp] = useState<FollowUp | null>(null);
  const [records, setRecords] = useState<FollowUpRecord[]>([]);
  const [recordLoading, setRecordLoading] = useState(false);

  // 加载详情数据
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const res = await followUpService.getFollowUp(Number(id));
        if (res.success && res.data) {
          setFollowUp(res.data);
        } else {
          message.error(res.message || '无法获取跟进详情');
        }
      } catch (e) {
        message.error('获取跟进详情时发生错误');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // 加载跟进记录
  useEffect(() => {
    const loadRecords = async () => {
      if (!id) return;
      setRecordLoading(true);
      try {
        const res = await followUpService.getRecords(Number(id));
        if (res.success) {
          setRecords(res.data || []);
        }
      } catch (e) {
        message.error('获取跟进记录失败');
      } finally {
        setRecordLoading(false);
      }
    };
    loadRecords();
  }, [id]);

  // 业务类型选项
  const serviceTypeOptions = SERVICE_TYPE_OPTIONS;


  const getServiceLabel = (value?: string) => {
    if (!value) return '-';
    const option = serviceTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getIntentLabel = (value?: string) => {
    if (!value) return '-';
    const option = INTENT_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getStatusLabel = (value?: string) => {
    if (!value) return '-';
    const option = STATUS_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const hasField = (fieldKey: keyof FollowUp): boolean => {
    return !!followUp && Object.prototype.hasOwnProperty.call(followUp, fieldKey);
  };

  const detailItems = followUp ? [
    { key: 'contact_name', label: '客户姓名', span: 1, content: followUp.contact_name || '-' },
    { key: 'contact_info', label: '联系方式', span: 1, content: followUp.contact_info || '-' },
    { key: 'nation', label: '国家', span: 1, content: followUp.nation || '-' },
    { key: 'service', label: '业务类型', span: 1, content: getServiceLabel(followUp.service) },
    { key: 'sub_service', label: '子业务名称', span: 2, content: followUp.sub_service || '-' },
    { key: 'customer_categories', label: '客户等级', span: 1, content: followUp.customer_categories || '-' },
    { key: 'source', label: '客户来源', span: 1, content: followUp.source || '-' },
    { key: 'intent', label: '意向程度', span: 1, content: getIntentLabel(followUp.intent) },
    { key: 'status', label: '跟进状态', span: 1, content: getStatusLabel(followUp.status) },
    { key: 'created_at', label: '获客日期', span: 1, content: followUp.created_at ? dayjs(followUp.created_at).format('YYYY-MM-DD') : '-' },
    { key: 'updated_at', label: '最后更新', span: 1, content: followUp.updated_at ? dayjs(followUp.updated_at).format('YYYY-MM-DD') : '-' },
    { key: 'remark', label: '跟进备注', span: 2, content: followUp.remark || '-' },
  ].filter(item => hasField(item.key as keyof FollowUp)) : [];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="跟进记录详情"
        extra={<Button onClick={() => navigate(-1)}>返回</Button>}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Descriptions bordered size="middle" column={2}>
            {detailItems.map(item => (
              <Descriptions.Item key={item.key} label={item.label} span={item.span}>
                {item.content}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Card>

      <Card title="跟进记录" style={{ marginTop: 16 }} loading={recordLoading}>
        {records.length === 0 ? (
          <div style={{ color: '#999' }}>暂无记录</div>
        ) : (
          records.map((item) => (
            <Card key={item.id} size="small" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{getStatusLabel(item.status) || '未标记状态'}</span>
                <span style={{ color: '#999', fontSize: 12 }}>{item.created_at ? dayjs(item.created_at).format('YYYY-MM-DD') : ''}</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', color: '#555' }}>{item.remark || '无备注'}</div>
            </Card>
          ))
        )}
      </Card>
    </div>
  );
};

export default FollowUpDetail;
